<template>
  <div
    ref="wrap"
    class="vacuum-map-wrap"
    :class="{ 'pin-mode': mode === 'pin-and-go' }"
    @wheel.prevent="onWheel"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @pointerleave="onPointerUp"
  >
    <canvas
      ref="canvas"
      class="vacuum-map-canvas"
      :style="canvasStyle"
    />

    <div class="marker-layer">
      <v-icon
        v-for="m in deviceMarkers"
        :key="m.id"
        class="device-marker"
        :class="{ 'device-on': m.on, 'device-clickable': mode === 'select' }"
        :style="m.style"
        :title="m.name"
        @pointerdown.stop
        @click.stop="onDeviceClick(m.device)"
      >{{ m.icon }}</v-icon>
      <v-icon
        v-for="m in routineMarkers"
        :key="'routine-' + m.id"
        class="routine-marker"
        :class="{ 'routine-active': m.active, 'device-clickable': mode === 'select' }"
        :style="m.style"
        :title="'Routine: ' + m.name"
        @pointerdown.stop
        @click.stop="onRoutineClick(m.routine)"
      >mdi-gesture-tap-button</v-icon>
      <v-icon
        v-if="chargerMarker"
        class="charger-marker"
        :style="chargerMarker"
      >mdi-home-lightning-bolt</v-icon>
      <v-icon
        v-if="gotoMarker"
        class="goto-marker"
        :style="gotoMarker"
      >mdi-map-marker-radius</v-icon>
      <v-icon
        v-if="robotMarker"
        class="robot-marker"
        :style="robotMarker"
      >mdi-robot-vacuum</v-icon>
    </div>

    <div
      v-if="mode === 'pin-and-go'"
      class="vacuum-map-banner"
    >
      <v-icon small left>mdi-map-marker-radius</v-icon>
      Pin & Go: Tippe auf einen Punkt — Schnuffel fährt hin.
    </div>
    <div
      v-if="mode === 'place-device'"
      class="vacuum-map-banner place-banner"
    >
      <v-icon small left>mdi-lightbulb-on-outline</v-icon>
      Tippe auf die Karte, um {{ placingName || 'das Gerät' }} zu platzieren.
    </div>

    <div class="vacuum-map-controls" v-if="parsed">
      <v-btn icon small dark @click="zoomBy(1.25)"><v-icon>mdi-plus</v-icon></v-btn>
      <v-btn icon small dark @click="zoomBy(0.8)"><v-icon>mdi-minus</v-icon></v-btn>
      <v-btn icon small dark @click="resetView"><v-icon>mdi-fit-to-page</v-icon></v-btn>
    </div>

    <div
      v-if="!parsed && !error"
      class="vacuum-map-empty"
    >
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-3 grey--text">Karte wird geladen …</div>
    </div>
    <div
      v-if="error"
      class="vacuum-map-empty"
    >
      <v-icon color="grey">mdi-map-marker-off</v-icon>
      <div class="mt-1 grey--text caption">{{ error }}</div>
    </div>
  </div>
</template>

<script>
import { parseRoborockMap, segmentColor, pixelToRgba } from '@/services/rrMap'
import { deviceIcon } from '@/services/devices'

const RENDER_SCALE = 4 // Pixel-Vergrößerung beim Zeichnen ins Canvas (kein User-Zoom)
const MIN_ZOOM = 0.4
const MAX_ZOOM = 8
const DRAG_THRESHOLD_PX = 5 // unterhalb davon zählt Pointerup als Klick, nicht als Drag

export default {
  name: 'VacuumMap',
  props: {
    mapBuffer: { type: ArrayBuffer, default: null },
    rooms: { type: Array, default: () => [] },
    robotPos: { type: Object, default: null },
    error: { type: String, default: '' },
    mode: { type: String, default: 'select' }, // 'select' | 'pin-and-go' | 'place-device'
    gotoTarget: { type: Object, default: null }, // {x_mm, y_mm} – zuletzt gesetztes Ziel
    devices: { type: Array, default: () => [] }, // platzierte Geräte mit map_position
    routines: { type: Array, default: () => [] }, // Routinen — platzierte werden als Trigger-Marker gerendert
    placingName: { type: String, default: '' }, // Anzeigename im place-device-Banner
  },
  data: () => ({
    parsed: null,
    zoom: 1,
    tx: 0,
    ty: 0,
    pointers: new Map(), // pointerId -> { x, y }
    drag: null,          // { startX, startY, baseTx, baseTy, moved }
    pinch: null,         // { startDist, startZoom, centerX, centerY }
  }),
  computed: {
    segmentColors() {
      const out = {}
      this.rooms.forEach(r => { out[r.segment_id] = segmentColor(r.segment_id) })
      return out
    },
    canvasStyle() {
      return {
        transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.zoom})`,
        transformOrigin: '0 0',
      }
    },
    robotMarker() {
      const robot = (this.parsed && this.parsed.robot) || this.robotPos
      return this.markerStyle(robot)
    },
    chargerMarker() {
      return this.markerStyle(this.parsed && this.parsed.charger)
    },
    gotoMarker() {
      return this.markerStyle(this.gotoTarget)
    },
    deviceMarkers() {
      // zoom/tx/ty fließen über markerStyle ein → Marker wandern beim Pan/Zoom mit.
      return (this.devices || [])
        .filter(d => d.map_position)
        .map(d => {
          const style = this.markerStyle({ x: d.map_position.x, y: d.map_position.y })
          if (!style) return null
          return { id: d.id, device: d, style, icon: deviceIcon(d), on: d.on, name: d.display_name }
        })
        .filter(Boolean)
    },
    routineMarkers() {
      return (this.routines || [])
        .filter(r => r.map_position)
        .map(r => {
          const style = this.markerStyle({ x: r.map_position.x, y: r.map_position.y })
          if (!style) return null
          return { id: r.id, routine: r, style, active: r.active, name: r.name }
        })
        .filter(Boolean)
    },
  },
  watch: {
    mapBuffer(buf) {
      if (buf) this.render()
    },
    gotoTarget() {
      if (this.mapBuffer) this.render()
    },
  },
  mounted() {
    if (this.mapBuffer) this.render()
    window.addEventListener('resize', this.fitToWrap)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.fitToWrap)
  },
  methods: {
    render() {
      try {
        const parsed = parseRoborockMap(this.mapBuffer)
        this.parsed = parsed
      } catch (e) {
        console.error('rrMap parse', e)
        return
      }
      const img = this.parsed.image
      if (!img) return

      const canvas = this.$refs.canvas
      const s = RENDER_SCALE
      const newW = img.width * s
      const newH = img.height * s
      const sizeChanged = canvas.width !== newW || canvas.height !== newH
      canvas.width = newW
      canvas.height = newH
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = false

      const off = document.createElement('canvas')
      off.width = img.width
      off.height = img.height
      const offCtx = off.getContext('2d')
      const imageData = offCtx.createImageData(img.width, img.height)
      for (let y = 0; y < img.height; y++) {
        for (let x = 0; x < img.width; x++) {
          const srcY = img.height - 1 - y
          const px = img.pixels[srcY * img.width + x]
          const rgba = pixelToRgba(px, this.segmentColors)
          const di = (y * img.width + x) * 4
          imageData.data[di] = rgba[0]
          imageData.data[di + 1] = rgba[1]
          imageData.data[di + 2] = rgba[2]
          imageData.data[di + 3] = rgba[3]
        }
      }
      offCtx.putImageData(imageData, 0, 0)
      ctx.drawImage(off, 0, 0, canvas.width, canvas.height)

      if (this.parsed.path && this.parsed.path.length > 1) {
        ctx.beginPath()
        ctx.strokeStyle = 'rgba(255,255,255,0.7)'
        ctx.lineWidth = Math.max(1, s * 0.4)
        this.parsed.path.forEach((p, i) => {
          const px = (p.x / 50 - img.left) * s
          const py = (img.height - 1 - (p.y / 50 - img.top)) * s
          if (i === 0) ctx.moveTo(px, py)
          else ctx.lineTo(px, py)
        })
        ctx.stroke()
      }

      // Roboter, Ladestation und Pin&Go-Ziel werden als HTML-Marker über das
      // Canvas gelegt (siehe template / markerStyle), damit sie beim Reinzoomen
      // nicht verpixeln und das Robot-Icon verwendet werden kann.

      if (sizeChanged) this.$nextTick(this.fitToWrap)
    },
    mmToCanvas(pt, img, s) {
      return {
        x: (pt.x / 50 - img.left) * s,
        y: (img.height - 1 - (pt.y / 50 - img.top)) * s,
      }
    },
    // mm im Roborock-Koordinatensystem → Wrap-CSS-Pixel (inkl. Pan + Zoom).
    markerStyle(mm) {
      if (!mm || !this.parsed || !this.parsed.image) return null
      const img = this.parsed.image
      const cp = this.mmToCanvas(mm, img, RENDER_SCALE)
      const cssX = cp.x * this.zoom + this.tx
      const cssY = cp.y * this.zoom + this.ty
      return { left: cssX + 'px', top: cssY + 'px' }
    },

    // ----- Zoom / Pan -----
    fitToWrap() {
      const wrap = this.$refs.wrap
      const canvas = this.$refs.canvas
      if (!wrap || !canvas || !canvas.width || !canvas.height) return
      const w = wrap.clientWidth
      const h = wrap.clientHeight
      const z = Math.min(w / canvas.width, h / canvas.height)
      this.zoom = z
      this.tx = (w - canvas.width * z) / 2
      this.ty = (h - canvas.height * z) / 2
    },
    resetView() {
      this.fitToWrap()
    },
    clampZoom(z) {
      return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))
    },
    zoomBy(factor) {
      const wrap = this.$refs.wrap
      const rect = wrap.getBoundingClientRect()
      this.zoomAt(rect.width / 2, rect.height / 2, factor)
    },
    // Skaliert um (cx, cy) im Wrap-Koordinatensystem.
    zoomAt(cx, cy, factor) {
      const newZoom = this.clampZoom(this.zoom * factor)
      const eff = newZoom / this.zoom
      this.tx = cx - (cx - this.tx) * eff
      this.ty = cy - (cy - this.ty) * eff
      this.zoom = newZoom
    },
    onWheel(e) {
      const wrap = this.$refs.wrap
      const rect = wrap.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
      this.zoomAt(cx, cy, factor)
    },

    // ----- Pointer (Maus + Touch über Pointer Events) -----
    onPointerDown(e) {
      this.$refs.wrap.setPointerCapture(e.pointerId)
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (this.pointers.size === 1) {
        this.drag = {
          startX: e.clientX,
          startY: e.clientY,
          baseTx: this.tx,
          baseTy: this.ty,
          moved: false,
          pointerId: e.pointerId,
        }
        this.pinch = null
      } else if (this.pointers.size === 2) {
        const pts = [...this.pointers.values()]
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
        const rect = this.$refs.wrap.getBoundingClientRect()
        this.pinch = {
          startDist: dist || 1,
          startZoom: this.zoom,
          centerX: (pts[0].x + pts[1].x) / 2 - rect.left,
          centerY: (pts[0].y + pts[1].y) / 2 - rect.top,
        }
        this.drag = null
      }
    },
    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (this.pinch && this.pointers.size === 2) {
        const pts = [...this.pointers.values()]
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
        const factor = (dist / this.pinch.startDist) * (this.pinch.startZoom / this.zoom)
        this.zoomAt(this.pinch.centerX, this.pinch.centerY, factor)
        return
      }

      if (this.drag && this.drag.pointerId === e.pointerId) {
        const dx = e.clientX - this.drag.startX
        const dy = e.clientY - this.drag.startY
        if (!this.drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
          this.drag.moved = true
        }
        if (this.drag.moved) {
          this.tx = this.drag.baseTx + dx
          this.ty = this.drag.baseTy + dy
        }
      }
    },
    onPointerUp(e) {
      const wasDrag = this.drag
      this.pointers.delete(e.pointerId)
      if (this.pointers.size < 2) this.pinch = null
      if (this.pointers.size === 0) {
        this.drag = null
        if (wasDrag && !wasDrag.moved) {
          // echter Klick → Segment-Hit-Test
          this.hitTest(e.clientX, e.clientY)
        }
      }
    },

    hitTest(clientX, clientY) {
      if (!this.parsed || !this.parsed.image) return
      const canvas = this.$refs.canvas
      const rect = canvas.getBoundingClientRect()
      // rect bezieht sich auf das transformierte Canvas, daher passt das Verhältnis.
      const cssX = (clientX - rect.left) / rect.width
      const cssY = (clientY - rect.top) / rect.height
      if (cssX < 0 || cssX > 1 || cssY < 0 || cssY > 1) return
      const img = this.parsed.image
      const px = Math.floor(cssX * img.width)
      const py = Math.floor(cssY * img.height)

      // 1) Roboter-Klick hat Priorität: ~24 CSS-Pixel um die Icon-Mitte.
      const robot = (this.parsed && this.parsed.robot) || this.robotPos
      if (robot) {
        const wrapRect = this.$refs.wrap.getBoundingClientRect()
        const rp = this.mmToCanvas(robot, img, RENDER_SCALE)
        const robotCssX = rp.x * this.zoom + this.tx + wrapRect.left
        const robotCssY = rp.y * this.zoom + this.ty + wrapRect.top
        if (Math.hypot(clientX - robotCssX, clientY - robotCssY) <= 24) {
          this.$emit('robot-click')
          return
        }
      }

      // 2) Geräte-Platzierung: Klick → mm-Koordinaten emittieren.
      //    Wände sind hier erlaubt — Wandlampen hängen nun mal an der Wand.
      if (this.mode === 'place-device') {
        const srcY = img.height - 1 - py
        this.$emit('place-device', { x: (img.left + px) * 50, y: (img.top + srcY) * 50 })
        return
      }

      // 3) Pin-and-Go: Klick → mm-Koordinaten emittieren (außer Wand).
      if (this.mode === 'pin-and-go') {
        const srcY = img.height - 1 - py
        const idx = srcY * img.width + px
        const val = idx >= 0 && idx < img.pixels.length ? img.pixels[idx] : 0
        if ((val & 0x07) === 1) {
          // Wand — Pin-and-Go macht hier keinen Sinn
          this.$emit('pin-and-go-invalid')
          return
        }
        const xMm = (img.left + px) * 50
        const yMm = (img.top + srcY) * 50
        this.$emit('pin-and-go', { x: xMm, y: yMm })
        return
      }

      // 4) Standard: Segment-Klick.
      const srcY = img.height - 1 - py
      const idx = srcY * img.width + px
      if (idx < 0 || idx >= img.pixels.length) return
      const val = img.pixels[idx]
      const type = val & 0x07
      const seg = val >> 3
      if (type === 7 && seg > 0) {
        this.$emit('select-segment', seg)
      }
    },

    onDeviceClick(device) {
      // Im Platzier-/Pin-Modus sollen Klicks auf die Karte gehen, nicht aufs Gerät.
      if (this.mode !== 'select') return
      this.$emit('device-click', device)
    },

    onRoutineClick(routine) {
      if (this.mode !== 'select') return
      this.$emit('routine-click', routine)
    },
  },
}
</script>

<style scoped>
.vacuum-map-wrap {
  position: relative;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  height: 50vh;
  min-height: 260px;
  max-height: 75vh;
  touch-action: none;
  cursor: grab;
  user-select: none;
}
.vacuum-map-wrap:active {
  cursor: grabbing;
}
.vacuum-map-wrap.pin-mode {
  cursor: crosshair;
}
.vacuum-map-banner {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  background: rgba(255,193,7,0.85);
  color: #000;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  text-align: center;
  pointer-events: none;
  z-index: 2;
}
.vacuum-map-canvas {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
  will-change: transform;
}
.vacuum-map-controls {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(0,0,0,0.55);
  border-radius: 6px;
  padding: 4px;
  z-index: 2;
}
.vacuum-map-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
}
.marker-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}
.marker-layer .v-icon {
  position: absolute;
  transform: translate(-50%, -50%);
  text-shadow: 0 0 4px rgba(0,0,0,0.85);
}
.robot-marker {
  font-size: 42px !important;
  color: #FF5252 !important;
}
.device-marker {
  font-size: 22px !important;
  color: #9E9E9E !important;
}
.device-marker.device-on {
  color: #FFD740 !important;
  text-shadow: 0 0 8px rgba(255,215,64,0.9);
}
.device-marker.device-clickable {
  pointer-events: auto;
  cursor: pointer;
}
.routine-marker {
  font-size: 26px !important;
  color: #9575CD !important;
}
.routine-marker.routine-active {
  color: #B388FF !important;
  text-shadow: 0 0 10px rgba(179,136,255,0.95);
}
.routine-marker.device-clickable {
  pointer-events: auto;
  cursor: pointer;
}
.place-banner {
  background: rgba(100,181,246,0.9);
}
.charger-marker {
  font-size: 26px !important;
  color: #4CAF50 !important;
}
.goto-marker {
  font-size: 34px !important;
  color: #FFD740 !important;
  animation: goto-pulse 1.2s ease-out infinite;
}
@keyframes goto-pulse {
  0%   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
  50%  { transform: translate(-50%, -50%) scale(1.15); opacity: 0.7; }
  100% { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
}
</style>
